import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../i18n/LanguageContext';
import api from '../../api';
import { staffNavItems as navItems, memberNavItems, applyMenuOrder } from '../../config/navigation';
import './Layout.css';

const Layout = () => {
    const { user, logout, isAuthenticated, hasPermission, roleOptions, switchRole } = useAuth();
    const { language, toggleLanguage, t } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [memberProfile, setMemberProfile] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const [recentCommunications, setRecentCommunications] = useState([]);
    const [commsExpanded, setCommsExpanded] = useState(false);
    const [accountingExpanded, setAccountingExpanded] = useState(location.pathname.startsWith('/accounting'));
    const [branding, setBranding] = useState({ organization_name: 'TANZANIA REVENUE WORKERS UNION', organization_short_name: 'TAREWU', logo_url: '/TAREWU_logo-2.png' });
    const [moduleVisibility, setModuleVisibility] = useState({});
    const [menuOrder, setMenuOrder] = useState({ member: [], staff: [] });
    const [accountingSectionExpanded, setAccountingSectionExpanded] = useState({ receivables: location.pathname.includes('receiv') || location.pathname.includes('customers'), payables: location.pathname.includes('payable') || location.pathname.includes('payments') || location.pathname.includes('suppliers'), banking: location.pathname.includes('/accounting/banking') });
    const userRoles = Array.isArray(user?.roles) && user.roles.length ? user.roles : [user?.role];
    const isMember = userRoles.includes('member');
    const visibilityGroup = userRoles.includes('admin') ? 'global' : userRoles.includes('secretary') ? 'secretary' : (userRoles.some(role => ['chair','treasurer','union_official','branch_leader','committee','auditor'].includes(role)) ? 'leaders' : 'member');
    const effectiveVisibility = moduleVisibility?.global ? (visibilityGroup === 'global' ? moduleVisibility.global : moduleVisibility.roles?.[visibilityGroup] || moduleVisibility.global) : moduleVisibility;

    useEffect(() => {
        if (!location.pathname.startsWith('/accounting')) return;
        setAccountingExpanded(true);
        if (location.pathname.includes('receiv') || location.pathname.includes('receipts') || location.pathname.includes('customers')) {
            setAccountingSectionExpanded(current => ({ ...current, receivables: true }));
        }
        if (location.pathname.includes('payable') || location.pathname.includes('payments') || location.pathname.includes('suppliers')) {
            setAccountingSectionExpanded(current => ({ ...current, payables: true }));
        }
        if (location.pathname.includes('/accounting/banking')) setAccountingSectionExpanded(current => ({ ...current, banking: true }));
    }, [location.pathname]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 992);
            if (window.innerWidth >= 992) {
                setSidebarOpen(true);
            } else {
                setSidebarOpen(false);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const update = data => setModuleVisibility(data || {});
        api.getModuleVisibility().then(response => update(response.data)).catch(() => {});
        const listener = event => update(event.detail);
        window.addEventListener('tarewu:modules-updated', listener);
        return () => window.removeEventListener('tarewu:modules-updated', listener);
    }, []);

    useEffect(() => {
        const update = data => setMenuOrder({ member: data?.member || [], staff: data?.staff || [] });
        api.getMenuOrder().then(response => update(response.data)).catch(() => {});
        const listener = event => update(event.detail);
        window.addEventListener('tarewu:menu-order-updated', listener);
        return () => window.removeEventListener('tarewu:menu-order-updated', listener);
    }, []);

    useEffect(() => {
        // Fetch member profile if user is a member
        if (user && isMember && user.member_number) {
            api.getMemberProfile()
                .then(res => {
                    if (res && res.data) {
                        setMemberProfile(res.data);
                    }
                })
                .catch(() => setMemberProfile(null));
        }
    }, [user, isMember]);

    // Fetch notifications
    useEffect(() => {
        if (!isAuthenticated) return;
        const controller = new AbortController();
        const fetchNotifications = async () => {
            try {
                const res = await api.getCommunications({ per_page: 20 }, { signal: controller.signal });
                if (res && res.data) {
                    // Filter for unread notifications
                    const allNotifications = Array.isArray(res.data) ? res.data : res.data.data || [];
                    setNotifications(allNotifications.slice(0, 10));
                }
            } catch (err) {
                if (err.name !== 'AbortError' && !err.isUnauthorized) console.warn('Notifications are temporarily unavailable.');
            }
        };
        fetchNotifications();
        return () => controller.abort();
    }, [isAuthenticated]);

    // Fetch recent communications for members sidebar
    useEffect(() => {
        const controller = new AbortController();
        if (user && isMember) {
            const fetchRecentComms = async () => {
                try {
                    const res = await api.getCommunications({ per_page: 5 }, { signal: controller.signal });
                    if (res && res.data) {
                        // Get sent communications only
                        const comms = Array.isArray(res.data) ? res.data : res.data.data || [];
                        const sentComms = comms.filter(c => c.status === 'sent');
                        setRecentCommunications(sentComms.slice(0, 5));
                    }
                } catch (err) {
                    if (err.name !== 'AbortError' && !err.isUnauthorized) console.warn('Recent communications are temporarily unavailable.');
                }
            };
            fetchRecentComms();
        }
        return () => controller.abort();
    }, [user, isAuthenticated, isMember]);

    useEffect(() => {
        const applyBranding = (data) => {
            if (!data) return;
            setBranding(current => ({ ...current, ...data }));
            if (data.primary_color) document.documentElement.style.setProperty('--brand-primary', data.primary_color);
            if (data.secondary_color) document.documentElement.style.setProperty('--brand-secondary', data.secondary_color);
            if (data.system_name) document.title = data.system_name;
        };
        api.getBrandingSettings().then(response => applyBranding(response.data)).catch(() => {});
        const updated = event => applyBranding(event.detail);
        window.addEventListener('tarewu:branding-updated', updated);
        return () => window.removeEventListener('tarewu:branding-updated', updated);
    }, []);

    const brandingLogo = branding.logo_url?.startsWith('http')
        ? branding.logo_url
        : `${api.baseUrl.replace(/\/api\/?$/, '')}${branding.logo_url || '/TAREWU_logo-2.png'}`;

    const handleLogout = () => {
        logout();
        navigate('/login');
    };



    const isMemberOnly = isMember && userRoles.length === 1;
    const staffNavItems = applyMenuOrder(navItems, menuOrder.staff).filter(item => {
        if (!user || !user.role) return false;
        return effectiveVisibility[item.module] !== false && hasPermission(item.permission);
    }).map(item => ({...item, children: item.children?.filter(child => !child.permission || hasPermission(child.permission))}));
    const memberVisibility = moduleVisibility?.roles?.member || effectiveVisibility;
    // Admins hide a menu for a person by denying its permission (Users & Roles → Permissions).
    const visibleMemberItems = applyMenuOrder(memberNavItems, menuOrder.member).filter(item => memberVisibility[item.module] !== false && (!item.permission || hasPermission(item.permission)));
    const allNavItems = isMemberOnly
        ? visibleMemberItems
        : isMember
            ? [{ path: '/profile', label: 'Member Profile', icon: '👤', module:'profiles' }, ...staffNavItems]
            : staffNavItems;
    // Pre-launch: Recruitment Committee members see only their profile and AI Screening until the portal launches.
    const filteredNavItems = user?.prelaunch_scope === 'screening'
        ? allNavItems.filter(item => ['/profile', '/staff-profile', '/recruitment/ai-screening'].includes(item.path))
        : allNavItems;

    const isActive = (path) => location.pathname === path || (path === '/accounting' && location.pathname.startsWith('/accounting/')) || (path === '/id-cards' && location.pathname.startsWith('/card-templates'));

    const roleLabel = (slug) => roleOptions.find((o) => o.slug === slug)?.name || slug;
    const handleSwitchRole = (slug) => {
        if (slug === user?.role) return;
        switchRole(slug);
        setUserMenuOpen(false);
        setMoreMenuOpen(false);
        navigate('/dashboard');
    };
    const roleSwitcher = roleOptions.length > 1 && (
        <div className="role-switcher">
            <span className="role-switcher-title">{t('Switch role')}</span>
            {roleOptions.map((option) => (
                <button key={option.slug} type="button" className={`role-switcher-item ${option.slug === user?.role ? 'active' : ''}`} onClick={() => handleSwitchRole(option.slug)}>
                    <span className="role-switcher-dot" aria-hidden="true"></span>
                    <span>{t(option.name)}</span>
                    {option.slug === user?.role && <span className="role-switcher-check" aria-hidden="true">✓</span>}
                </button>
            ))}
        </div>
    );

    const getInitials = (name) => {
        if (!name) return 'U';
        const parts = name.split(' ');
        return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
    };

    if (!isAuthenticated) {
        return <Outlet />;
    }

    return (
        <div className="layout">
            {/* Top Header */}
            <header className="header">
                <div className="header-left">
                    {isMobile && (
                        <button 
                            className="menu-btn"
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="3" y1="6" x2="21" y2="6"></line>
                                <line x1="3" y1="12" x2="21" y2="12"></line>
                                <line x1="3" y1="18" x2="21" y2="18"></line>
                            </svg>
                        </button>
                    )}
                    <Link to="/dashboard" className="logo">
                        <div className="logo-icon-wrapper">
                            <img src={brandingLogo} alt={branding.organization_short_name} className="logo-icon" style={{width: '40px', height: '40px', objectFit: 'contain'}} />
                        </div>
                        <span className="logo-text">{branding.organization_name}</span>
                        <span className="logo-text-short">{branding.organization_short_name}</span>
                    </Link>
                </div>

                <div className="header-center">
                    <div className="search-box">
                        <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        <input type="text" placeholder={t('Search...')} className="search-input" />
                    </div>
                </div>

                <div className="header-right">
                    {/* Language switch */}
                    <button
                        type="button"
                        className="header-action-btn lang-switch"
                        onClick={toggleLanguage}
                        title={language === 'en' ? 'Badili kwenda Kiswahili' : 'Switch to English'}
                    >
                        {language === 'en' ? 'EN' : 'SW'}
                    </button>
                    {/* Notifications Dropdown */}
                    <div className="notifications-container">
                        <button 
                            className="header-action-btn"
                            onClick={() => setNotificationsOpen(!notificationsOpen)}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                            </svg>
                            {notifications.length > 0 && (
                                <span className="notification-badge">{notifications.length}</span>
                            )}
                        </button>
                        
                        {notificationsOpen && (
                            <div className="notifications-dropdown">
                                <div className="notifications-header">
                                    <span>{t('Notifications')}</span>
                                    <span className="notifications-count">{notifications.length} {t('new')}</span>
                                </div>
                                <div className="notifications-list">
                                    {notifications.length > 0 ? (
                                        notifications.map((notification, index) => (
                                            <div key={notification.id || index} className="notification-item">
                                                <div className="notification-icon">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                                                        <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                                                    </svg>
                                                </div>
                                                <div className="notification-content">
                                                    <p className="notification-title">{notification.title || t('Notification')}</p>
                                                    <p className="notification-message">{notification.message || notification.content || ''}</p>
                                                    <span className="notification-time">
                                                        {notification.created_at ? new Date(notification.created_at).toLocaleDateString('sw-TZ') : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="no-notifications">
                                            <p>{t('No notifications')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* User Menu */}
                    <div className="user-menu-container">
                        <button 
                            className="user-profile-btn"
                            onClick={() => setUserMenuOpen(!userMenuOpen)}
                        >
                            <div className="user-avatar">
                                {user?.name ? getInitials(user.name) : 'U'}
                            </div>
                            <div className="user-details">
                                <span className="user-name">{user?.name || t('User')}</span>
                                <span className="user-role">{t(roleLabel(user?.role))}</span>
                            </div>
                            <svg className={`chevron ${userMenuOpen ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                        </button>
                        
                        {userMenuOpen && (
                            <div className="user-dropdown">
                                <div className="dropdown-header">
                                    <div className="dropdown-user-avatar">
                                        {user?.name ? getInitials(user.name) : 'U'}
                                    </div>
                                    <div className="dropdown-user-info">
                                        <span className="dropdown-user-name">{user?.name}</span>
                                        <span className="dropdown-user-email">{user?.email}</span>
                                        <span className="dropdown-user-role">{t(roleLabel(user?.role))}</span>
                                    </div>
                                </div>
                                {roleSwitcher && <><div className="dropdown-divider"></div>{roleSwitcher}</>}
                                <div className="dropdown-divider"></div>
                                <Link to={user?.role === 'member' ? '/profile' : '/staff-profile'} className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="12" cy="7" r="4"></circle>
                                    </svg>
                                    {t('My Profile')}
                                </Link>
                                {user?.role === 'admin' && <Link to="/settings" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="3"></circle>
                                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                                    </svg>
                                    {t('Settings')}
                                </Link>}
                                <div className="dropdown-divider"></div>
                                <button className="dropdown-item logout" onClick={handleLogout}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                        <polyline points="16 17 21 12 16 7"></polyline>
                                        <line x1="21" y1="12" x2="9" y2="12"></line>
                                    </svg>
                                    {t('Logout')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* Sidebar (Desktop) */}
            {!isMobile && (
                <aside className="sidebar">
                    <div className="sidebar-header">
                        <span className="sidebar-title">{t('MAIN MENU')}</span>
                    </div>
                    
                    {/* Profile Picture Section */}
                    <Link to={user?.role === 'member' ? '/profile' : '/staff-profile'} className="sidebar-profile-card">
                        <div className="sidebar-profile-avatar-large">
                            {memberProfile?.photo ? (
                                <img src={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080'}/uploads/members/${memberProfile.photo}`} alt="Profile" />
                            ) : (
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                            )}
                        </div>
                    </Link>
                    
                    <nav className="nav">
                        {filteredNavItems.map((item) => item.children ? <div className={`nav-item-group accounting-nav-group ${item.section ? 'has-section' : ''}`} data-section={item.section || undefined} key={item.path}>
                            <button className={`nav-item nav-expand-btn ${isActive(item.path) ? 'active' : ''}`} onClick={() => setAccountingExpanded(!accountingExpanded)}><span className="nav-icon-wrapper"><span className="nav-icon">{item.icon}</span></span><span className="nav-label">{t(item.label)}</span><span className={`expand-icon ${accountingExpanded ? 'open' : ''}`}>⌄</span></button>
                            {accountingExpanded && <div className="nav-submenu accounting-submenu">{item.children.map(child => child.children ? <div className="accounting-subgroup" key={child.key}><button className={`accounting-subgroup-button ${child.children.some(link => location.pathname === (link.activePath || link.path)) ? 'active' : ''}`} onClick={() => setAccountingSectionExpanded(s => ({ ...s, [child.key]: !s[child.key] }))}><span>{t(child.label)}</span><b>{accountingSectionExpanded[child.key] ? '−' : '+'}</b></button>{accountingSectionExpanded[child.key] && <div className="accounting-subgroup-links">{child.children.map(link => <Link key={link.path} to={link.path} className={`nav-submenu-item ${location.pathname === (link.activePath || link.path) ? 'active' : ''}`}>{t(link.label)}</Link>)}</div>}</div> : <Link key={child.path} to={child.path} className={`nav-submenu-item ${location.pathname === child.path ? 'active' : ''}`}><span>{t(child.label)}</span></Link>)}</div>}
                        </div> : <Link key={item.path} to={item.path} data-section={item.section || undefined} className={`nav-item ${item.section ? 'has-section' : ''} ${isActive(item.path) ? 'active' : ''}`}><span className="nav-icon-wrapper"><span className="nav-icon">{item.icon}</span></span><span className="nav-label">{t(item.label)}</span></Link>)}
                        
                        {/* Communications Menu Item for Members */}
                        {user && user.role === 'member' && recentCommunications.length > 0 && (
                            <div className="nav-item-group">
                                <button 
                                    className={`nav-item nav-expand-btn ${commsExpanded ? 'expanded' : ''}`}
                                    onClick={() => setCommsExpanded(!commsExpanded)}
                                >
                                    <span className="nav-icon-wrapper">
                                        <span className="nav-icon">📢</span>
                                    </span>
                                    <span className="nav-label">{t('Announcements')}</span>
                                    <span className={`expand-icon ${commsExpanded ? 'open' : ''}`}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="6 9 12 15 18 9"></polyline>
                                        </svg>
                                    </span>
                                </button>
                                
                                {/* Expanded Communications List */}
                                {commsExpanded && (
                                    <div className="nav-submenu">
                                        {recentCommunications.map((comm) => (
                                            <Link 
                                                key={comm.id}
                                                to={`/communications`}
                                                className="nav-submenu-item"
                                            >
                                                <span className="submenu-badge">{comm.message_type === 'announcement' ? '📣' : '📱'}</span>
                                                <span className="submenu-text">
                                                    <span className="submenu-title">{comm.title}</span>
                                                    <span className="submenu-date">
                                                        {new Date(comm.created_at).toLocaleDateString('sw-TZ', { month: 'short', day: 'numeric' })}
                                                    </span>
                                                </span>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </nav>
                </aside>
            )}

            {/* Mobile Sidebar Overlay */}
            {isMobile && sidebarOpen && (
                <div 
                    className="sidebar-overlay"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Mobile Sidebar */}
            {isMobile && (
                <aside className={`sidebar mobile-sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="sidebar-header">
                        <div className="mobile-sidebar-logo">
                            <img src={brandingLogo} alt={branding.organization_short_name} className="logo-icon" style={{width: '40px', height: '40px', objectFit: 'contain'}} />
                            <span className="logo-text">{branding.organization_name}</span>
                        </div>
                        <button className="close-sidebar-btn" onClick={() => setSidebarOpen(false)}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    
                    <nav className="nav">
                        {filteredNavItems.map((item) => item.children ? <div className={`nav-item-group accounting-nav-group ${item.section ? 'has-section' : ''}`} data-section={item.section || undefined} key={item.path}><button className={`nav-item nav-expand-btn ${isActive(item.path) ? 'active' : ''}`} onClick={() => setAccountingExpanded(!accountingExpanded)}><span className="nav-icon">{item.icon}</span><span className="nav-label">{t(item.label)}</span><span className={`expand-icon ${accountingExpanded ? 'open' : ''}`}>⌄</span></button>{accountingExpanded && <div className="nav-submenu accounting-submenu">{item.children.map(child => child.children ? <div className="accounting-subgroup" key={child.key}><button className={`accounting-subgroup-button ${child.children.some(link => location.pathname === (link.activePath || link.path)) ? 'active' : ''}`} onClick={() => setAccountingSectionExpanded(s => ({ ...s, [child.key]: !s[child.key] }))}><span>{t(child.label)}</span><b>{accountingSectionExpanded[child.key] ? '−' : '+'}</b></button>{accountingSectionExpanded[child.key] && <div className="accounting-subgroup-links">{child.children.map(link => <Link key={link.path} to={link.path} className={`nav-submenu-item ${location.pathname === (link.activePath || link.path) ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>{t(link.label)}</Link>)}</div>}</div> : <Link key={child.path} to={child.path} className={`nav-submenu-item ${location.pathname === child.path ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>{t(child.label)}</Link>)}</div>}</div> : <Link key={item.path} to={item.path} data-section={item.section || undefined} className={`nav-item ${item.section ? 'has-section' : ''} ${isActive(item.path) ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}><span className="nav-icon">{item.icon}</span><span className="nav-label">{t(item.label)}</span></Link>)}
                    </nav>
                </aside>
            )}

            {/* Main Content */}
            <main className="main-content">
                <Outlet />
            </main>

            {/* Mobile Bottom Navigation */}
            {isMobile && moreMenuOpen && <div className="more-menu-overlay" onClick={() => setMoreMenuOpen(false)}>
                <section className="more-menu-sheet" onClick={event => event.stopPropagation()}>
                    <div className="more-menu-handle"></div>
                    <div className="more-menu-header"><div><h3>{t('More services')}</h3><p>{t('Select a service')}</p></div><button type="button" onClick={() => setMoreMenuOpen(false)} aria-label={t('Close')}>×</button></div>
                    {roleSwitcher}
                    <div className="more-menu-grid">{filteredNavItems.slice(4).map(item => <Link key={item.path} to={item.path} className={`more-menu-card ${isActive(item.path) ? 'active' : ''}`} onClick={() => setMoreMenuOpen(false)}><span className="more-menu-icon">{item.icon}</span><span>{t(item.label)}</span></Link>)}</div>
                </section>
            </div>}
            {isMobile && (
                <nav className="bottom-nav">
                    {filteredNavItems.slice(0, 4).map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`bottom-nav-item ${isActive(item.path) ? 'active' : ''}`}
                        >
                            <span className="bottom-nav-icon">{item.icon}</span>
                            <span className="bottom-nav-label">{t(item.label)}</span>
                        </Link>
                    ))}
                    <button
                        type="button"
                        className={`bottom-nav-item bottom-nav-more ${filteredNavItems.slice(4).some(item => isActive(item.path)) ? 'active' : ''}`}
                        onClick={() => setMoreMenuOpen(true)}
                        aria-label={t('More menu')}
                    >
                        <span className="bottom-nav-icon more-dots"><i></i><i></i><i></i></span>
                        <span className="bottom-nav-label">{t('More')}</span>
                    </button>
                </nav>
            )}
        </div>
    );
};

export default Layout;
